import { EMPTY_LIST } from '../../../../constants/common'

import {
  getRequestProfileCountersSectionsByChanges,
  getRequestProfileReloadsByChange,
} from './useRequestProfileReloadAfterChange.utils'

/**
 * Exposes the single reload entry point for request-profile mutations: call
 * sites declare the semantic change (see `REQUEST_PROFILE_CHANGE_TYPE`) and
 * the hook fans out to every affected data source, so they never hand-pick
 * loader lists. The change-to-reloads topology lives in
 * {@link getRequestProfileReloadsByChange}; the tab counters are reloaded once
 * per call with the union of the changes' sections.
 *
 * @usage
 * ```
 *  const { handleReloadAfterChange } = useRequestProfileContext()
 *
 *  handleReloadAfterChange(REQUEST_PROFILE_CHANGE_TYPE.relatedRequests)
 * ```
 */
export function useRequestProfileReloadAfterChange({
  onLoadCounters,
  onReloadList,
  ...reloadHandlers
}) {
  const reloadsByChange = getRequestProfileReloadsByChange(reloadHandlers)

  async function handleReloadAfterChange(...changes) {
    if (changes.some(change => reloadsByChange[change]?.withListReload)) {
      onReloadList?.()
    }

    const reloads = new Set(
      changes.flatMap(change => reloadsByChange[change]?.reloads ?? EMPTY_LIST)
    )

    const countersSections = getRequestProfileCountersSectionsByChanges({
      changes,
      reloadsByChange,
    })

    return Promise.allSettled([
      ...[...reloads].map(reload => reload()),
      ...(countersSections ? [onLoadCounters(countersSections)] : EMPTY_LIST),
    ])
  }

  return { handleReloadAfterChange }
}
